import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  ADMIN_RECOVERY_PASSWORD_MIN_LENGTH,
  validateAdminPassword,
} from '../utils/passwordPolicy';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ShieldWarning, ArrowLeft, Eye, EyeSlash, Key } from '@phosphor-icons/react';

const BACKEND_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;

const AdminRecoveryPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    reset_secret: '',
    admin_email: 'admin@azaibilling.com',
    new_password: '',
    confirm_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) =>
    setFormData((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    if (formData.reset_secret.length < 32) {
      toast.error('Reset secret must be at least 32 characters');
      return false;
    }
    if (!formData.admin_email) {
      toast.error('Admin email is required');
      return false;
    }
    if (formData.new_password.length < ADMIN_RECOVERY_PASSWORD_MIN_LENGTH) {
      toast.error(`New password must be at least ${ADMIN_RECOVERY_PASSWORD_MIN_LENGTH} characters`);
      return false;
    }
    const passwordError = validateAdminPassword(formData.new_password, {
      minLength: ADMIN_RECOVERY_PASSWORD_MIN_LENGTH,
    });
    if (passwordError) {
      toast.error('Password must contain an uppercase, lowercase, and number');
      return false;
    }
    if (formData.new_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/auth/admin-reset`,
        {
          reset_secret: formData.reset_secret,
          admin_email: formData.admin_email,
          new_password: formData.new_password,
        },
        { withCredentials: false }
      );
      const count = resp.data?.accounts_reset || 1;
      // Mandatory MFA: the endpoint no longer auto-issues a session.
      toast.success(
        count > 1
          ? `Password reset for ${count} admin rows. Sign in with your new password — MFA still applies.`
          : 'Password reset. Sign in with your new password — MFA still applies.'
      );
      setTimeout(() => navigate('/login'), 800);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      if (status === 503) {
        toast.error('Admin reset is not enabled on this server. Ask your deployment admin to set ADMIN_RESET_SECRET.');
      } else if (status === 403) {
        toast.error('Invalid reset secret');
      } else if (status === 404) {
        toast.error('No AZAI admin account found for that email');
      } else if (status === 429) {
        toast.error('Too many attempts. Wait an hour before trying again.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Reset failed. Check your inputs.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
          data-testid="back-to-login"
        >
          <ArrowLeft size={16} /> Back to sign in
        </Link>

        <Card className="border-amber-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-white border-b border-amber-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <ShieldWarning size={22} className="text-amber-700" weight="fill" />
              </div>
              <div>
                <CardTitle className="text-xl">AZAI Admin Password Recovery</CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Emergency reset — audit-logged and rate-limited
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 mb-5 space-y-1">
              <p className="font-semibold">Before you use this form, you need:</p>
              <ol className="list-decimal list-inside space-y-0.5 pl-1">
                <li>
                  <code className="bg-white px-1 py-0.5 rounded border border-amber-200 text-[11px]">
                    ADMIN_RESET_SECRET
                  </code>{' '}
                  set on the server (deployment dashboard → Environment Variables), minimum 32 chars.
                </li>
                <li>A fresh, unused secret value (generate one below if needed).</li>
                <li>
                  After reset, <strong>rotate the secret</strong> so this form is disabled until the next emergency.
                </li>
              </ol>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="admin_email" className="text-xs uppercase tracking-wide text-slate-500">
                  Admin Email
                </Label>
                <Input
                  id="admin_email"
                  name="admin_email"
                  type="email"
                  value={formData.admin_email}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  data-testid="admin-email-input"
                />
              </div>

              <div>
                <Label htmlFor="reset_secret" className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <Key size={12} /> Reset Secret (from server env)
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="reset_secret"
                    name="reset_secret"
                    type={showSecret ? 'text' : 'password'}
                    value={formData.reset_secret}
                    onChange={handleChange}
                    required
                    placeholder="32+ character value of ADMIN_RESET_SECRET"
                    className="pr-9 font-mono text-sm"
                    data-testid="reset-secret-input"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showSecret ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {formData.reset_secret.length}/32 chars min
                </p>
              </div>

              <div>
                <Label htmlFor="new_password" className="text-xs uppercase tracking-wide text-slate-500">
                  New Admin Password
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="new_password"
                    name="new_password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.new_password}
                    onChange={handleChange}
                    required
                    placeholder="Min 12 chars, 1 upper, 1 lower, 1 number"
                    className="pr-9"
                    data-testid="new-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm_password" className="text-xs uppercase tracking-wide text-slate-500">
                  Confirm New Password
                </Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  className="mt-1"
                  data-testid="confirm-password-input"
                  autoComplete="new-password"
                />
                {formData.confirm_password &&
                  formData.new_password !== formData.confirm_password && (
                    <p className="text-[11px] text-red-600 mt-1">Passwords don't match</p>
                  )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white"
                data-testid="admin-reset-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Reset admin password'
                )}
              </Button>
            </form>

            <details className="mt-5 text-xs text-slate-500">
              <summary className="cursor-pointer hover:text-slate-700">
                Need a new secret? Generate one locally
              </summary>
              <p className="mt-2">
                Run this on your computer, then paste the output into your server environment variables and redeploy:
              </p>
              <pre className="mt-2 bg-slate-100 border border-slate-200 rounded p-2 text-[11px] font-mono text-slate-700 overflow-x-auto">
python3 -c "import secrets; print(secrets.token_urlsafe(48))"
              </pre>
            </details>
          </CardContent>
        </Card>

        <p className="text-[10px] text-slate-400 text-center mt-4">
          Every attempt is recorded in the admin audit log. Three failed attempts per hour will temporarily block this form.
        </p>
      </div>
    </div>
  );
};

export default AdminRecoveryPage;
